/**
 * Promo banner shown across public pages (Landing, Pricing, Legal, Contact).
 * Controlled by a single PROMO config below. Set `enabled: false` to retire it.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";

const PROMO = {
  enabled: true,
  code: "WELCOME40",
  percent: 40,
  // Local-date end (Australia/Adelaide-style — we don't actually localize, just display).
  endsAt: new Date("2026-06-30T23:59:59"),
  blurb: "off every plan, every month",
};

// localStorage usage here stores only a non-sensitive "dismissed" flag for the
// promo banner. No credentials or PII are persisted client-side.
const DISMISS_KEY = "rt_promo_dismissed_welcome40_v1";

const formatCountdown = (ms) => {
  if (ms <= 0) return "ends soon";
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  if (days >= 2) return `${days} days left`;
  if (days === 1) return `1 day, ${hours}h left`;
  if (hours >= 1) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

const PromoBanner = () => {
  const [dismissed, setDismissed] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (typeof window === "undefined") return;
    setDismissed(window.localStorage.getItem(DISMISS_KEY) === "1");
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  if (!PROMO.enabled) return null;
  const msLeft = PROMO.endsAt.getTime() - now.getTime();
  if (msLeft <= 0) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(DISMISS_KEY, "1");
    }
  };

  return (
    <div
      data-testid="promo-banner"
      className="bg-[#1F2326] text-[#F8F6F1] relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 flex items-center gap-3 text-sm">
        <Sparkles className="w-4 h-4 text-[#B89B5E] flex-none" strokeWidth={2.25} />
        <div className="flex-1 truncate">
          <span className="font-semibold text-[#F8F6F1]">
            {PROMO.percent}% {PROMO.blurb}
          </span>
          <span className="hidden sm:inline text-[#C9C2B0] ml-2">
            Use code{" "}
            <span className="font-mono font-semibold text-[#B89B5E]">
              {PROMO.code}
            </span>{" "}
            at checkout.
          </span>
          <span className="ml-3 text-xs text-[#B89B5E] font-medium" data-testid="promo-countdown">
            {formatCountdown(msLeft)}
          </span>
        </div>
        <Link
          to="/pricing"
          data-testid="promo-cta"
          className="hidden sm:inline-flex items-center gap-1 bg-[#B89B5E] text-[#1F2326] hover:bg-[#A78946] px-3 py-1 rounded-md font-semibold text-xs transition-colors flex-none"
        >
          Claim offer
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="promo-dismiss"
          aria-label="Dismiss"
          className="text-[#C9C2B0] hover:text-[#F8F6F1] flex-none p-1 rounded-md hover:bg-white/5"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// Expose config so other components (Pricing strike-through) can read it.
export const PROMO_CONFIG = PROMO;
export default PromoBanner;
